import { createFileRoute } from "@tanstack/react-router";
import { PageWrapper } from "@/components/PageWrapper.tsx";
import { Loader } from "@/components/Loader.tsx";
import { FetchingError } from "@/components/FetchingError.tsx";
import { t } from "@lingui/core/macro";
import { PageHeader } from "@/components/PageHeader.tsx";
import { TransactionDetail } from "@/modules/sepaPayments/TransactionDetail.tsx";
import { useSepaTransactionQuery } from "@/modules/sepaPayments/api/useSepaTransactions.ts";
import { AllBankDataProvider, useBankData } from "@/dataProviders/BankDataProvider.tsx";
import { getLocation } from "@/lib/table.ts";
import { SEPA_LOCATION_KEY } from "@/routes/payments/sepa/index.tsx";

export const Route = createFileRoute("/payments/sepa/$id")({
  component: RouteComponent,
});

function RouteComponent() {
  return (
    <AllBankDataProvider>
      <SepaDetailContent />
    </AllBankDataProvider>
  );
}

function SepaDetailContent() {
  const { id } = Route.useParams();
  const title = t`SEPA payments detail`;
  const { bankData } = useBankData();
  const { data, isError, isPending, error } = useSepaTransactionQuery(
    {
      merchantTransactionId: id,
    },
    { enabled: !!bankData },
  );

  const logoUrl = bankData[data?.paymentProvider ?? ""]?.logoUrl;

  return (
    <PageWrapper>
      {isError && (
        <FetchingError
          message={t`Something went wrong. We were unable to load the data. Please refresh the page and try again.`}
          error={error}
        />
      )}
      {isPending && <Loader message={t`Loading sepa payment...`} />}
      {data && (
        <>
          <PageHeader
            breadcrumbs={[
              { href: `/payments/sepa/${getLocation(SEPA_LOCATION_KEY)}`, title: t`SEPA payments` },
              { href: "/", title },
            ]}
          />
          {bankData && (
            <TransactionDetail
              amount={data.amount ?? 0}
              application={data.applicationName ?? ""}
              bankId={data.bankAuthId ?? ""}
              callbackUrl={data.callbackUrl ?? ""}
              clientId={data.clientId ?? ""}
              createdAt={data.createdAt}
              creditorIban={data.creditorIban ?? ""}
              creditorName={data.creditorName ?? ""}
              currency={data.currency ?? ""}
              customerId={data.merchantTransactionId ?? ""}
              debtorIban={data.debtorIban ?? ""}
              debtorName={data.debtorName ?? ""}
              description={data.description ?? ""}
              endToEndReference={data.endToEndIdentification ?? ""}
              finbricksId={data.transactionId ?? ""}
              flowId={data.frontendFlowId ?? ""}
              initiator={data.initiatorName ?? ""}
              logoUrl={logoUrl}
              merchantId={data.merchantId ?? ""}
              operationId={data.operationId ?? ""}
              paymentProvider={data.paymentProvider ?? ""}
              priority={data.instructionPriority ?? ""}
              transactionStatus={data.resultCode ?? "OPENED"}
              updatedAt={data.updatedAt}
            />
          )}
        </>
      )}
    </PageWrapper>
  );
}
